import { AnalyticsEventData } from '../analytics/AnalyticsManager'
import {
  HOST_PAGE_EVENTS_MSG_TO_APP,
  HostPageEventsMsgToApp,
} from '../messenger/messenger.messages'
import HostPageEventQueue from './host-page-event.queue'
import {
  HOST_PAGE_MPARTICLE_EVENT_TYPES,
  HOST_PAGE_MPARTICLE_SURFACE,
  HostPageEventInput,
  HostPageMParticleEventInfo,
  HostPageMParticleEventType,
  QueuedHostPageEvent,
} from './host-page-event.types'

export interface HostPageEventTrackerProps {
  queue?: HostPageEventQueue
  sendAnalyticsEvent?: (eventName: string, eventData: AnalyticsEventData) => void
  sendMessage: (message: HostPageEventsMsgToApp) => void
}

interface TrackHostPageEventOptions {
  flush?: boolean
}

type TrackableHostPageEvent = Omit<HostPageEventInput, 'mparticle'>

interface TrackHostPageEventWithMParticleProps extends TrackableHostPageEvent {
  mparticleEventInfo?: HostPageMParticleEventInfo
  mparticleEventName: string
  mparticleEventType?: HostPageMParticleEventType
}

export default class HostPageEventTracker {
  private queue: HostPageEventQueue
  private sendAnalyticsEvent?: (eventName: string, eventData: AnalyticsEventData) => void
  private sendMessage: (message: HostPageEventsMsgToApp) => void

  constructor(props: HostPageEventTrackerProps) {
    this.queue = props.queue || new HostPageEventQueue()
    this.sendAnalyticsEvent = props.sendAnalyticsEvent
    this.sendMessage = props.sendMessage
  }

  public track = (
    event: HostPageEventInput,
    options: TrackHostPageEventOptions = {}
  ): QueuedHostPageEvent => {
    const queuedEvent = this.queue.record(event)

    this.sendAnalyticsEvent?.(event.name, event.data)

    if (options.flush) {
      this.flush(event.campaignId)
    }

    return queuedEvent
  }

  public trackEvent = (
    event: TrackHostPageEventWithMParticleProps,
    options: TrackHostPageEventOptions = {}
  ): QueuedHostPageEvent => {
    return this.trackWithMParticleTracker('event', event, options)
  }

  public trackFunnelEvent = (
    event: TrackHostPageEventWithMParticleProps,
    options: TrackHostPageEventOptions = {}
  ): QueuedHostPageEvent => {
    return this.trackWithMParticleTracker('funnel', event, options)
  }

  public trackUIElement = (
    event: TrackHostPageEventWithMParticleProps,
    options: TrackHostPageEventOptions = {}
  ): QueuedHostPageEvent => {
    return this.trackWithMParticleTracker('ui-element', event, options)
  }

  public flush = (campaignId: string): void => {
    const events = this.queue.drain(campaignId)

    if (events.length === 0) {
      return
    }

    this.sendMessage({
      type: HOST_PAGE_EVENTS_MSG_TO_APP,
      payload: {
        campaignId,
        events,
      },
    })
  }

  public peek = (campaignId: string): QueuedHostPageEvent[] => {
    return this.queue.peek(campaignId)
  }

  public reset = (): void => {
    this.queue.reset()
  }

  private trackWithMParticleTracker = (
    tracker: 'event' | 'funnel' | 'ui-element',
    event: TrackHostPageEventWithMParticleProps,
    options: TrackHostPageEventOptions
  ): QueuedHostPageEvent => {
    const {
      mparticleEventInfo,
      mparticleEventName,
      mparticleEventType = HOST_PAGE_MPARTICLE_EVENT_TYPES.OTHER,
      ...hostPageEvent
    } = event

    return this.track(
      {
        ...hostPageEvent,
        mparticle: {
          eventInfo: {
            ...mparticleEventInfo,
            surface: HOST_PAGE_MPARTICLE_SURFACE,
          },
          eventName: mparticleEventName,
          eventType: mparticleEventType,
          tracker,
        },
      },
      options
    )
  }
}
